import { Button, ButtonVariants } from '@triumph/design-system';

import { Dialog } from '@/components/shared/components/Dialog';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    FormActions,
    ModalSubtitle,
    PromptModalContainer,
    ReadonlyConditionTitle,
} from './ApplicantConditions.StyledComponents';
import { Condition } from './ApplicantConditions.types';

interface Props {
    condition: Condition | null;
    visible: boolean;
    isAnother?: boolean;
    onSkip: () => void;
    onConfirm: () => void;
}

export const ConditionFollowUpDialog = ({
    condition,
    visible,
    isAnother = false,
    onSkip,
    onConfirm,
}: Props) => {
    const { isConditionTextFormattingEnabled } = useFeatureFlags();

    return (
        <Dialog
            id="condition-follow-up-prompt-modal"
            header={`Create ${isAnother ? 'Another' : 'a'} Follow-Up Task/Condition?`}
            visible={visible}
            onHide={onSkip}
            dismissableMask={false}
            data-testid="condition-follow-up-prompt-dialog"
        >
            <PromptModalContainer>
                <ModalSubtitle>
                    This task/condition was flagged as problematic. Would you like to create{' '}
                    {isAnother ? 'another' : 'a new'} Task/Condition to track the follow-up work?
                </ModalSubtitle>

                <ReadonlyConditionTitle
                    $markdown={isConditionTextFormattingEnabled}
                    data-testid="condition-follow-up-prompt-description"
                >
                    {isConditionTextFormattingEnabled ? (
                        <FormattedText>{condition?.description ?? ''}</FormattedText>
                    ) : (
                        <Linkify>{condition?.description ?? ''}</Linkify>
                    )}
                </ReadonlyConditionTitle>

                <FormActions style={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant={ButtonVariants.secondary}
                        onClick={onSkip}
                        data-testid="condition-follow-up-prompt-skip"
                    >
                        No, I'm Done
                    </Button>
                    <Button
                        variant={ButtonVariants.primary}
                        onClick={onConfirm}
                        data-testid="condition-follow-up-prompt-confirm"
                    >
                        Yes, Add {isAnother ? 'Another ' : ''}Follow-Up Task/Condition
                    </Button>
                </FormActions>
            </PromptModalContainer>
        </Dialog>
    );
};
